import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import SectionTitle from './SectionTitle';
import projects, { categories } from '../assets/data/projects';
import ProjectItem from './ProjectItem';
import ProjectFilter from './ProjectFilter';
const ProjectSectionStyle = styled.div`
  padding: 10rem 0;
  
  .projects__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 3rem;
    margin-top: 5rem;
  }
  
  .projects__slider {
    margin-top: 5rem;
    position: relative;
    cursor: grab;
    
    &:active {
      cursor: grabbing;
    }
  }
  
  .swiper-container {
    max-width: 100%;
  }
  
  .swipe-hint {
    text-align: center;
    margin-top: 3rem;
    color: var(--text-secondary);
    font-size: 1.4rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    opacity: 0.7;
    animation: fadeInUp 0.6s ease-out 0.5s both;
    
    svg {
      width: 24px;
      height: 24px;
      animation: swipeAnimation 2s ease-in-out infinite;
    }
  }
  
  @keyframes swipeAnimation {
    0%, 100% {
      transform: translateX(-5px);
    }
    50% {
      transform: translateX(5px);
    }
  }
  
  @media only screen and (max-width: 768px) {
    padding: 8rem 0;
    
    .projects__grid {
      grid-template-columns: 1fr;
      gap: 3rem;
      margin-top: 4rem;
    }
    
    .projects__slider {
      margin-top: 4rem;
    }
    
    .swiper-button-prev,
    .swiper-button-next {
      display: none;
    }
  }
  
  @media only screen and (max-width: 500px) {
    .projects__grid {
      grid-template-columns: 1fr;
    }
  }
`;
export default function ProjectSection() {
  const [activeCategory, setActiveCategory] = useState('all');
  
  // Filter projects based on active category
  const filteredProjects = useMemo(() => {
    if (activeCategory === 'all') {
      return projects;
    }
    return projects.filter(project => 
      project.category.toLowerCase().replace(' ', '-') === activeCategory
    );
  }, [activeCategory]);
  
  // Calculate project counts for each category
  const projectCounts = useMemo(() => {
    const counts = { all: projects.length };
    
    projects.forEach(project => {
      const categoryId = project.category.toLowerCase().replace(' ', '-');
      counts[categoryId] = (counts[categoryId] || 0) + 1;
    });
    
    return counts;
  }, []);
  
  return (
    <ProjectSectionStyle>
      <div className="container">
        <SectionTitle 
          heading="Featured Projects" 
          subheading="Explore my work across different categories" 
        />
        
        <ProjectFilter 
          categories={categories}
          activeCategory={activeCategory}
          onFilterChange={setActiveCategory}
          projectCounts={projectCounts}
        />
        
        {activeCategory === 'all' ? (
          <div className="projects__slider">
            <Swiper
              key={activeCategory}
              spaceBetween={30}
              slidesPerView={2}
              breakpoints={{
                0: {
                  slidesPerView: 1,
                },
                768: {
                  slidesPerView: 2,
                },
                1200: {
                  slidesPerView: 3,
                },
              }}
            >
              {filteredProjects.map((project) => (
                <SwiperSlide key={project.id}>
                  <ProjectItem
                    title={project.name}
                    img={project.img}
                    description={project.desc}
                    link={project.link}
                    category={project.category}
                    tech={project.tech}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
            <div className="swipe-hint">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
              <span>Swipe to explore more projects</span>
            </div>
          </div>
        ) : (
          <div className="projects__grid">
            {filteredProjects.map((project) => (
              <ProjectItem
                key={project.id}
                title={project.name}
                img={project.img}
                description={project.desc}
                link={project.link}
                category={project.category}
                tech={project.tech}
              />
            ))}
          </div>
        )}
      </div>
    </ProjectSectionStyle>
  );
}
